// =============================================================================
// AdminAutomationsPage — system automation catalog (Automations tab)
// =============================================================================
// Lists the seeded transactional automations (welcome, verification, password
// reset, invites, order/payment lifecycle). Admins can edit the subject line
// and toggle non-critical automations on/off. The email *structure* stays
// code-defined for deliverability; critical security mail can never be
// disabled. The merge variables available to each subject are documented inline.
// =============================================================================

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission } from '@tkpod/shared';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  ModalFooter,
  Spinner,
} from '@tkpod/ui';

import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/lib/toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Template {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  type: string;
  category: string;
  subject: string | null;
  body: string | null;
  variablesSchema: Record<string, string> | null;
  isSystemTemplate: boolean;
  isActive: boolean;
  updatedAt: string;
}

interface EditState {
  slug: string;
  name: string;
  description: string | null;
  subject: string;
  isActive: boolean;
  critical: boolean;
  variablesSchema: Record<string, string> | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Critical security/lifecycle automations — editable subject, but the active
 * toggle is locked on (mirrors the server-side CRITICAL_SLUGS guard so the UI
 * never offers an action the API will reject).
 */
const CRITICAL_SLUGS = new Set([
  'email-verification',
  'password-reset',
  'password-changed',
  'member-invite',
  'admin-invite',
]);

const queryKeys = { templates: ['admin', 'notification-templates'] as const };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminAutomationsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canManage = hasPermission(user?.permissions ?? [], 'notification:manage');

  const [edit, setEdit] = useState<EditState | null>(null);

  const listQuery = useQuery<Template[], Error>({
    queryKey: queryKeys.templates,
    queryFn: () =>
      api.get<Template[]>('/api/v1/admin/notifications/templates'),
  });

  const updateMutation = useMutation<
    Template,
    Error,
    { slug: string; subject: string; isActive: boolean }
  >({
    mutationFn: ({ slug, subject, isActive }) =>
      api.patch<Template>(`/api/v1/admin/notifications/templates/${slug}`, {
        subject,
        isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates });
      setEdit(null);
      toast('Automation updated.');
    },
    onError: (err) => toast(err.message, 'error'),
  });

  const toggleMutation = useMutation<
    Template,
    Error,
    { slug: string; isActive: boolean }
  >({
    mutationFn: ({ slug, isActive }) =>
      api.patch<Template>(`/api/v1/admin/notifications/templates/${slug}`, {
        isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates });
      toast('Automation status updated.');
    },
    onError: (err) => toast(err.message, 'error'),
  });

  const openEdit = useCallback((t: Template) => {
    setEdit({
      slug: t.slug,
      name: t.name,
      description: t.description,
      subject: t.subject ?? '',
      isActive: t.isActive,
      critical: CRITICAL_SLUGS.has(t.slug),
      variablesSchema: t.variablesSchema,
    });
  }, []);

  const templates = listQuery.data ?? [];

  if (listQuery.isLoading && !listQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading automations..." />
      </div>
    );
  }

  if (listQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-danger">
            Failed to load automations: {listQuery.error.message}
          </p>
          <Button variant="outline" size="sm" onClick={() => void listQuery.refetch()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Email automations"
          description="System-triggered transactional emails. Edit the subject line or pause non-critical automations. Email content is code-defined for deliverability; critical security mail cannot be disabled."
        />

        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          {templates.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
              No automations seeded.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Automation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {templates.map((t) => {
                  const critical = CRITICAL_SLUGS.has(t.slug);
                  return (
                    <tr key={t.id} className="hover:bg-surface-secondary">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-text-primary">
                            {t.name}
                          </p>
                          {critical && (
                            <Badge variant="warning" size="sm">
                              Critical
                            </Badge>
                          )}
                        </div>
                        {t.description && (
                          <p className="text-xs text-text-tertiary">
                            {t.description}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <code className="text-xs text-text-secondary">
                          {t.subject ?? '—'}
                        </code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {t.isActive ? (
                          <Badge variant="success" size="sm" dot>
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="default" size="sm" dot>
                            Paused
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canManage && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openEdit(t)}
                            >
                              Edit
                            </Button>
                          )}
                          {canManage && !critical && (
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={
                                toggleMutation.isPending &&
                                toggleMutation.variables?.slug === t.slug
                              }
                              onClick={() =>
                                toggleMutation.mutate({
                                  slug: t.slug,
                                  isActive: !t.isActive,
                                })
                              }
                            >
                              {t.isActive ? 'Pause' : 'Resume'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Modal
        open={edit !== null}
        onClose={() => setEdit(null)}
        title={edit ? `Edit — ${edit.name}` : 'Edit automation'}
        size="lg"
      >
        {edit && (
          <div className="space-y-4">
            {edit.description && (
              <p className="text-sm text-text-secondary">{edit.description}</p>
            )}

            <Input
              label="Subject line"
              value={edit.subject}
              onChange={(e) =>
                setEdit((prev) => (prev ? { ...prev, subject: e.target.value } : prev))
              }
              maxLength={300}
              hint="Use {{variable}} tokens — see available variables below."
            />

            {edit.variablesSchema && Object.keys(edit.variablesSchema).length > 0 && (
              <div className="rounded-md border border-border bg-surface-secondary px-4 py-3">
                <p className="text-xs font-medium text-text-primary mb-2">
                  Available variables
                </p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(edit.variablesSchema).map(([key, kind]) => (
                    <code
                      key={key}
                      className="rounded bg-surface px-2 py-1 text-xs text-text-secondary border border-border"
                      title={kind}
                    >
                      {`{{${key}}}`}
                    </code>
                  ))}
                </div>
              </div>
            )}

            {!edit.critical && (
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <button
                  type="button"
                  role="switch"
                  aria-checked={edit.isActive}
                  onClick={() =>
                    setEdit((prev) =>
                      prev ? { ...prev, isActive: !prev.isActive } : prev,
                    )
                  }
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                    edit.isActive ? 'bg-brand-600' : 'bg-border'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ${
                      edit.isActive ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                {edit.isActive ? 'Active' : 'Paused'}
              </label>
            )}

            {edit.critical && (
              <p className="rounded-md bg-warning/10 px-4 py-3 text-xs text-warning-700">
                This is a critical security/lifecycle automation and cannot be
                paused.
              </p>
            )}

            <ModalFooter>
              <Button variant="outline" onClick={() => setEdit(null)}>
                Cancel
              </Button>
              <Button
                onClick={() =>
                  updateMutation.mutate({
                    slug: edit.slug,
                    subject: edit.subject.trim(),
                    isActive: edit.critical ? true : edit.isActive,
                  })
                }
                loading={updateMutation.isPending}
                disabled={!edit.subject.trim()}
              >
                Save changes
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
    </div>
  );
}
